import React from 'react';
import styled from 'styled-components';
import {NavLink} from 'react-router-dom';
import {getStoreDisplayName} from 'interfaces/Stores';
import {
  clearPendingStoreHcDeviceSearch,
  writePendingStoreHcDeviceSearch,
} from '../../utils/storeHcPendingSearch';

const StyledSearchSuggestion = styled.ul`
  padding-left: 0px;
  margin: 0px;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  overflow: hidden;
  box-shadow: 0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14),
    0px 1px 3px 0px rgba(0, 0, 0, 0.12);
`;

const StyledSearchItems = styled.li`
  background-color: ${props => props.theme.cardBg};
  color: ${props => props.theme.altText};
  padding: 0px;
  list-style: none;
  margin-bottom: 0px;
  overflow: hidden;
  &:last-of-type {
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
  }
`;

const StyledNavLink = styled(NavLink)`
  display: block;
  font-size: 0.8rem;
  color: ${props => props.theme.subtleText};
  text-decoration: none;
  width: calc(100% - 63px);
  padding: 8px 10px 8px 55px;
  &:visited {
    color: ${props => props.theme.subtleText};
    text-decoration: none;
  }
  &:hover,
  &:focus {
    color: ${props => props.theme.altText};
    text-decoration: none;
    background-color: ${props => props.theme.tableRow};
  }
  ${StyledSearchItems}:first-of-type > & {
    color: ${props => props.theme.altText};
    background-color: ${props => props.theme.tableRow};
  }
`;

const DeviceRowPrimary = styled.span`
  display: inline;
`;

const DeviceRowStoreContext = styled.span`
  display: inline;
  margin-left: 6px;
  color: ${props => props.theme.subtleText};
  opacity: 0.75;
  font-size: 0.75rem;
`;

function buildLinkTarget(result) {
  const selectedRegion = sessionStorage.getItem('storeHealth:selectedRegion');
  if (result.kind === 'device') {
    return {
      pathname: `/store/${result.store.SiteId}/retail-hardware-status`,
      state: {
        selectedRegion,
        searchedDevice: {
          deviceType: result.device.deviceType,
          serialNumber: result.device.serialNumber,
          deviceName: result.device.deviceName,
        },
      },
    };
  }
  return {
    pathname: `/store/${result.store.SiteId}/retail-hardware-status`,
    state: {selectedRegion},
  };
}

function renderResultLabel(result) {
  if (result.kind === 'device') {
    const serial = result.device.serialNumber || 'Unknown';
    const name = result.device.deviceName || 'Unknown';
    return (
      <>
        <DeviceRowPrimary>{`${serial} - ${name}`}</DeviceRowPrimary>
        <DeviceRowStoreContext>{`-  ${getStoreDisplayName(result.store)}`}</DeviceRowStoreContext>
      </>
    );
  }
  return getStoreDisplayName(result.store);
}

function buildResultKey(result, index) {
  if (result.kind === 'device') {
    return `d-${result.store.SiteId}-${result.device.serialNumber}-${index}`;
  }
  return `s-${result.store.StoreNumber}-${index}`;
}

/** Safe for HTML id / CSS selectors: no spaces; only [A-Za-z0-9_-]. */
function sanitizeForDomIdSegment(value) {
  const s = String(value ?? '')
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return s.length ? s : 'unknown';
}

function buildLinkId(result, index) {
  if (result.kind === 'device') {
    const site = sanitizeForDomIdSegment(result.store.SiteId);
    const serial = sanitizeForDomIdSegment(result.device.serialNumber);
    return `d${site}-${serial}-${index}`;
  }
  return `s${sanitizeForDomIdSegment(result.store.StoreNumber)}-${index}`;
}

function SearchSuggestionsStoreHealth(props) {
  const {results = []} = props;
  const numberOfResultsToDisplay = 8;

  return (
    <StyledSearchSuggestion>
      {results.slice(0, numberOfResultsToDisplay).map((result, index) => (
        <StyledSearchItems key={buildResultKey(result, index)}>
          <StyledNavLink
            id={buildLinkId(result, index)}
            to={buildLinkTarget(result)}
            exact
            onClick={() => {
              if (result.kind === 'device') {
                writePendingStoreHcDeviceSearch({
                  siteId: String(result.store.SiteId),
                  selectedRegion: sessionStorage.getItem('storeHealth:selectedRegion'),
                  searchedDevice: {
                    deviceType: result.device.deviceType,
                    serialNumber: result.device.serialNumber,
                    deviceName: result.device.deviceName,
                  },
                });
              } else {
                clearPendingStoreHcDeviceSearch();
              }
            }}
          >
            {renderResultLabel(result)}
          </StyledNavLink>
        </StyledSearchItems>
      ))}
    </StyledSearchSuggestion>
  );
}

export default SearchSuggestionsStoreHealth;
